import { apiClient } from '@/lib/apiClient'
import type { ApiResponse } from '@/types/api.types'

// ─────────────────────────────────────────────────────────────
// Annunci admin → operatori (bacheca unidirezionale + email opzionale)
// ─────────────────────────────────────────────────────────────

export type AnnouncementSeverity = 'INFO' | 'IMPORTANT'
export type AnnouncementAudience = 'PUBLISHED_ONLY' | 'ALL'
export type AnnouncementStatus   = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'

export interface AnnouncementEmailSummary {
  operatorsScanned: number
  emailsSent:       number
  skipped:          number
  errors:           number
}

/** Vista admin: tutti i campi. */
export interface Announcement {
  annId:        string
  title:        string
  body:         string
  severity:     AnnouncementSeverity
  audience:     AnnouncementAudience
  emailFlag:    boolean
  annStatus:    AnnouncementStatus
  createdAt:    string
  updatedAt:    string
  publishedAt?: string
  createdBy:    string
  readCount:    number
  emailStartedAt?: string
  emailSummary?:   AnnouncementEmailSummary
}

/** Vista operatore: campi pubblici + flag di lettura. */
export interface OperatorAnnouncement {
  annId:       string
  title:       string
  body:        string
  severity:    AnnouncementSeverity
  publishedAt: string
  read:        boolean
}

export interface AnnouncementPayload {
  title:     string
  body:      string
  severity:  AnnouncementSeverity
  audience:  AnnouncementAudience
  emailFlag: boolean
}

// ── Admin ────────────────────────────────────────────────────

export async function listAdminAnnouncements(): Promise<Announcement[]> {
  const { data } = await apiClient.get<ApiResponse<{ items: Announcement[]; total: number }>>(
    '/admin/announcements',
  )
  return data.data.items
}

export async function createAnnouncement(payload: AnnouncementPayload): Promise<Announcement> {
  const { data } = await apiClient.post<ApiResponse<Announcement>>('/admin/announcements', payload)
  return data.data
}

export async function updateAnnouncement(
  annId: string,
  payload: Partial<AnnouncementPayload>,
): Promise<Announcement> {
  const { data } = await apiClient.put<ApiResponse<Announcement>>(
    `/admin/announcements/${encodeURIComponent(annId)}`,
    payload,
  )
  return data.data
}

/** Bozza → eliminata; pubblicato → archiviato (sparisce in-app, resta in cronologia). */
export async function deleteAnnouncement(annId: string): Promise<void> {
  await apiClient.delete(`/admin/announcements/${encodeURIComponent(annId)}`)
}

/** Pubblica una bozza; se emailFlag è attivo il backend schedula il fan-out email. */
export async function publishAnnouncement(annId: string): Promise<Announcement> {
  const { data } = await apiClient.post<ApiResponse<Announcement>>(
    `/admin/announcements/${encodeURIComponent(annId)}/publish`,
  )
  return data.data
}

// ── Operatore ────────────────────────────────────────────────

export async function listMyAnnouncements(): Promise<OperatorAnnouncement[]> {
  const { data } = await apiClient.get<ApiResponse<{ items: OperatorAnnouncement[]; total: number }>>(
    '/me/announcements',
  )
  return data.data.items
}

export async function getUnreadAnnouncementsCount(): Promise<number> {
  const { data } = await apiClient.get<ApiResponse<{ count: number }>>(
    '/me/announcements/unread-count',
  )
  return data.data.count
}

export async function markAnnouncementRead(annId: string): Promise<void> {
  await apiClient.post(`/me/announcements/${encodeURIComponent(annId)}/read`)
}
